import {
  WorkInstructionControlRoomDto,
  WorkInstructionDto,
  WorkInstructionStatus,
} from '@operations/app/api'
import { CraneSplitDialogUIStore } from '@operations/features/craneSplit/stores/crane-split-dialog.ui-store'
import { useTranslate } from '@tolgee/react'
import { ButtonOption, OptionsButton } from '@tom-ui/ui'
import { computed } from 'mobx'
import { observer } from 'mobx-react-lite'
import { appStore } from 'modules/utils/src/stores/AppStore'
import {
  canStartWorkInstruction,
  canStopWorkInstruction,
} from '../../helpers/work-instruction-table.helper'
import { WorkInstructionUIStore } from '../../stores/work-instruction.ui-store'

type WorkInstructionMenuOptionsProps =
  | {
      type: 'controlRoom'
      workInstruction: WorkInstructionControlRoomDto
      uiStore: WorkInstructionUIStore
    }
  | {
      type: 'craneSplit'
      workInstruction: WorkInstructionDto
      uiStore: CraneSplitDialogUIStore
    }
export const WorkInstructionMenuOptions = observer(
  ({ type, workInstruction, uiStore }: WorkInstructionMenuOptionsProps) => {
    const { t } = useTranslate()

    const actions = computed(() => {
      const actions: ButtonOption[] = []

      if (canStopWorkInstruction(workInstruction.status) && uiStore.canStartOrStop) {
        actions.push({
          label: t('stop', 'Stop'),
          onClick: async () => {
            try {
              if (type === 'controlRoom') {
                await uiStore.stopWorkInstructions([workInstruction])
              } else {
                await uiStore.stopWorkInstructions([workInstruction])
              }
              appStore.setShowAlert(
                'success',
                t('workInstructionStopped', 'Work instruction stopped'),
              )
            } catch (error) {
              appStore.setShowAlert(
                'error',
                t('errorStoppingWorkInstruction', 'Error stopping work instruction'),
              )
            }
          },
          testId: 'work-instruction-stop',
        })
      } else if (canStartWorkInstruction(workInstruction.status) && uiStore.canStartOrStop) {
        actions.push({
          label:
            workInstruction.status === WorkInstructionStatus.Stopped
              ? t('resume', 'Resume')
              : t('start', 'Start'),
          onClick: async () => {
            try {
              if (type === 'controlRoom') {
                await uiStore.startWorkInstruction([workInstruction])
              } else {
                await uiStore.startWorkInstruction([workInstruction])
              }
              appStore.setShowAlert(
                'success',
                t('workInstructionStarted', 'Work instruction started'),
              )
            } catch (error) {
              appStore.setShowAlert(
                'error',
                t('errorStartingWorkInstruction', 'Error starting work instruction'),
              )
            }
          },
          testId: 'work-instruction-start',
        })
      }

      if (workInstruction.isPriority && workInstruction.status !== WorkInstructionStatus.Finished) {
        actions.push({
          label: t('setDefaultPriority', 'Set Default Priority'),
          onClick: async () => {
            try {
              if (type === 'controlRoom') {
                await uiStore.setPriority([workInstruction.id], !workInstruction.isPriority)
              } else {
                await uiStore.setPriority([workInstruction.id], !workInstruction.isPriority)
              }
              appStore.setShowAlert('success', t('prioritySetToDefault', 'Priority set to default'))
            } catch (error) {
              appStore.setShowAlert(
                'error',
                t('errorSettingDefaultPriority', 'Error setting default priority'),
              )
            }
          },
          testId: 'work-instruction-set-default-priority',
        })
      } else if (workInstruction.status !== WorkInstructionStatus.Finished) {
        actions.push({
          label: t('setHighPriority', 'Set High Priority'),
          onClick: async () => {
            try {
              if (type === 'controlRoom') {
                await uiStore.setPriority([workInstruction.id], !workInstruction.isPriority)
              } else {
                await uiStore.setPriority([workInstruction.id], !workInstruction.isPriority)
              }
              appStore.setShowAlert('success', t('prioritySetToHigh', 'Priority set to high'))
            } catch (error) {
              appStore.setShowAlert(
                'error',
                t('errorSettingHighPriority', 'Error setting high priority'),
              )
            }
          },
          testId: 'work-instruction-set-high-priority',
        })
      }

      return actions
    }).get()

    if (actions.length === 0) {
      return null
    }

    return <OptionsButton options={actions} position='right' testId='order-options-button' />
  },
)
